import React, { useEffect, useRef, useState } from "react";
import { Play, X } from "lucide-react";
import { trackEvent } from "../lib/analytics";

export const DEMO_VIDEO_SRC = "/video/totalreach-demo.mp4";
export const DEMO_VIDEO_POSTER = "/video/poster.jpg";

// Hero "Watch the tour" button + full-screen player modal (native <video>, keyboard/Escape closable).
export const DemoVideo = ({ className = "", poster = false }) => {
  const [open, setOpen] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open]);

  const show = () => { trackEvent("demo_video_open", { source: "landing_hero" }); setOpen(true); };
  const hide = () => {
    const v = videoRef.current;
    if (v) trackEvent("demo_video_close", { seconds: Math.round(v.currentTime || 0), pct: v.duration ? Math.round((v.currentTime / v.duration) * 100) : 0 });
    setOpen(false);
  };

  return (
    <>
      <button type="button" onClick={show} data-testid="hero-watch-demo-btn"
        className={`group relative inline-flex items-center gap-3 rounded-full border border-white/20 pl-1.5 pr-6 py-1.5 text-white hover:border-cyan-400/50 hover:text-cyan-300 transition-colors ${className}`}>
        <span className="relative inline-flex w-9 h-9 items-center justify-center rounded-full bg-cyan-400 text-slate-900 shadow-[0_0_0_6px_rgb(var(--brand-400)/0.18)] group-hover:scale-105 transition-transform">
          <Play className="w-4 h-4 ml-0.5" fill="currentColor" aria-hidden="true" />
        </span>
        <span className="text-sm font-semibold">Watch the 3-minute tour</span>
      </button>

      {poster && (
        <button type="button" onClick={show} aria-label="Play the product tour" data-testid="hero-demo-poster"
          className="group hidden lg:block absolute right-6 top-24 w-[440px] xl:w-[520px] rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/40 rotate-[1.5deg] hover:rotate-0 transition-transform duration-300">
          <img src={DEMO_VIDEO_POSTER} alt="TotalReach Mission Control dashboard" className="w-full aspect-video object-cover" loading="lazy" />
          <span className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-cyan-400 text-slate-900 shadow-[0_0_0_10px_rgb(var(--brand-400)/0.2)] group-hover:scale-110 transition-transform">
              <Play className="w-6 h-6 ml-1" fill="currentColor" aria-hidden="true" />
            </span>
          </span>
          <span className="absolute left-4 bottom-3 text-left text-white"><span className="block text-sm font-semibold">See the whole platform in 3 minutes</span><span className="block text-xs text-slate-300">Advertise · Fundraise · Race intelligence · War Room</span></span>
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 sm:p-8" role="dialog" aria-modal="true" aria-label="TotalReach product tour"
          onClick={hide} data-testid="demo-video-modal">
          <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={hide} aria-label="Close video" data-testid="demo-video-close"
              className="absolute -top-11 right-0 inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white">
              <X className="w-4 h-4" /> Close
            </button>
            <video ref={videoRef} src={DEMO_VIDEO_SRC} poster={DEMO_VIDEO_POSTER} controls autoPlay playsInline preload="metadata"
              data-testid="demo-video-player" className="w-full aspect-video rounded-2xl border border-white/10 bg-black shadow-2xl"
              onEnded={() => trackEvent("demo_video_complete", { source: "landing_hero" })}>
              Your browser can't play this video. <a href={DEMO_VIDEO_SRC} className="underline">Download the tour</a>.
            </video>
            <p className="mt-3 text-center text-xs text-slate-400">Narrated tour · 3:38 · No sign-up needed</p>
          </div>
        </div>
      )}
    </>
  );
};
